import type { IOrder, ISamplePool, NegativeControl, Sample } from '../Model/index.js'
import { isSampleIncomplete } from '../Model/index.js'

/**
 * Order state is a frontend-only summary of where an order stands, and it is drawn from two different
 * places. Splitting the union says which, in the type rather than in a comment.
 */

/**
 * Decided by the order's own fields, so any context that has an order can work these out — including
 * the list endpoints, which return no pools.
 */
export type OrderStateFromOrder =
  | 'DONE' /* order has been fully processed */
  | 'DELETED' /* order has been soft deleted */
  | 'POSTPROCESSING' /* order is going through postprocessing */
  | 'PREPROCESSING' /* order is currently being preprocessed */
  | 'DRAFT' /* order is a draft */
  | 'PENDING' /* order is still being worked on by the lab */

/**
 * Decided by the order's sample pools and the pipeline information hanging off their items, neither of
 * which belongs to the order. Only a view that has loaded the pools can reach these, which is why a
 * list cannot show them.
 */
export type PoolDerivedOrderState =
  | 'PREPROCESSED_BLOCKED' /* order has been preprocessed, but is now blocked due to errors or quality issues */
  | 'READY_FOR_POSTPROCESSING' /* preprocessing complete, awaiting user confirmation for postprocessing */
  | 'READY_FOR_PREPROCESSING' /* order is complete enough for preprocessing */

/**
 * `UNKNOWN` is for the window where the pools have not arrived yet and the order's own fields do not
 * settle the question. Reporting it beats guessing: the guess was `PENDING`, which reads as "the lab is
 * still working on it" and was shown for orders that were in fact preprocessed or blocked.
 */
export type OrderState = OrderStateFromOrder | PoolDerivedOrderState | 'UNKNOWN'

/**
 * What a list can reach: the order's own fields, plus the two states the `hasBlockingItems` flag the
 * list endpoints carry is enough to tell apart.
 *
 * `READY_FOR_PREPROCESSING` is missing by decision, not by omission. It asks whether every sample is
 * complete, and completeness includes `customerSampleName`, which is not a column on the pool item —
 * V21 dropped it — and lives only on SampleMetadata, where the codebase holds two mutually
 * inconsistent rules for which of a sample's metadata rows is the current one. A second flag would
 * have to pick one and would then be wrong wherever the other applies, so the list shows such an
 * order as `PENDING` and the detail page remains the only place that says READY_FOR_PREPROCESSING.
 */
export type OrderStateFromList = OrderStateFromOrder | 'PREPROCESSED_BLOCKED' | 'READY_FOR_POSTPROCESSING'

export type PoolItemState = 'CREATED' | 'PROCESSED_OK' | 'ERROR' | 'PROCESSED_BAD' | 'UNKNOWN' | 'PROCESSING'

export function determineOrderStateFromOrder(order: IOrder): OrderStateFromOrder {
  if (order.orderDone) return 'DONE'
  if (order.deletedAt) return 'DELETED'
  if (order.orderPostprocessingStarted) return 'POSTPROCESSING'
  if (order.orderSubmission && !order.orderPreprocessed) return 'PREPROCESSING'
  if (order.type === 'draft') return 'DRAFT'
  return 'PENDING'
}

const awaitsPostprocessingDecision = (order: IOrder): boolean => !order.orderPostprocessingStarted && !!order.orderPreprocessed

/**
 * `'LOADING'` is a distinct input rather than an empty pool list: an empty list is a real answer, and
 * passing one while the pools are still in flight is what made an unloaded order look `PENDING`.
 */
export function determineOrderState(order: IOrder, samplePools: ISamplePool[] | 'LOADING'): OrderState {
  const os = determineOrderStateFromOrder(order)
  if (os != 'PENDING') return os
  /* the order's own fields did not settle it, so without the pools there is nothing to say yet */
  if (samplePools === 'LOADING') return 'UNKNOWN'
  if (awaitsPostprocessingDecision(order)) {
    if (samplePools.some((x) => x.samples.some((item) => isBlocking(item)) || x.negativeControls.some((item) => isBlocking(item)))) {
      return 'PREPROCESSED_BLOCKED'
    }
    return 'READY_FOR_POSTPROCESSING'
  }
  if (checkOrderReady(samplePools)) {
    return 'READY_FOR_PREPROCESSING'
  } else return 'PENDING'
}

/**
 * The list counterpart of {@link determineOrderState}: same order-field logic, and `hasBlockingItems`
 * from the list endpoint standing in for the pools it does not return. `determineOrderState` stays the
 * definition of what blocking means — the backend aggregate behind the flag mirrors `isBlocking` here,
 * and both read the same fork through {@link awaitsPostprocessingDecision}.
 *
 * See {@link OrderStateFromList} for why a list stops short of READY_FOR_PREPROCESSING.
 */
export function determineOrderStateForList(order: IOrder, hasBlockingItems: boolean): OrderStateFromList {
  const os = determineOrderStateFromOrder(order)
  if (os != 'PENDING') return os
  if (awaitsPostprocessingDecision(order)) {
    return hasBlockingItems ? 'PREPROCESSED_BLOCKED' : 'READY_FOR_POSTPROCESSING'
  }
  return 'PENDING'
}

export function isBlocking(item: Sample | NegativeControl): boolean {
  const state = determinePoolItemState(item)
  return state === 'PROCESSED_BAD' || state === 'ERROR'
}

export function determinePoolItemState(item: Sample | NegativeControl): PoolItemState {
  if (!item.pipelineInformation?.submittedAt) return 'CREATED'
  if (item.pipelineInformation?.errorAt) return 'ERROR'
  if (item.pipelineInformation?.submittedAt && !item.pipelineInformation?.errorAt && !item.pipelineInformation?.completedAt)
    return 'PROCESSING'
  if (item.pipelineInformation?.completedAt && item.pipelineInformation?.qualityVerifiedAt) return 'PROCESSED_OK'
  if (item.pipelineInformation?.completedAt && !item.pipelineInformation?.qualityVerifiedAt) return 'PROCESSED_BAD'
  else return 'UNKNOWN'
}

function checkOrderReady(samplePools: ISamplePool[]): boolean {
  if (samplePools.length == 0) return false

  if (samplePools.map((s) => s.samples.length).reduce((a, b) => a + b, 0) === 0) {
    return false
  }

  return (
    samplePools
      ?.filter((s) => s.samples?.length > 0)
      ?.map((d) => !d.samples.find((s) => isSampleIncomplete(s)))
      ?.reduce((a, b) => a && b, true) &&
    samplePools?.map((sp) => !sp.negativeControls.find((nc) => isSampleIncomplete(nc)))?.reduce((a, b) => a && b, true)
  )
}
